/**
 * <header-nav> — hover intent for the desktop navigation.
 *
 * This does not replace Dawn's disclosure logic, it drives it. Opening and
 * closing goes through `details.open`, so the `toggle` event still fires and
 * Dawn's `HeaderMenu` (assets/details-disclosure.js) keeps doing its job:
 * setting `--header-bottom-position-desktop`, holding the sticky header open,
 * and closing the panel on focusout. Click and keyboard therefore behave
 * exactly as they do without this file loaded.
 *
 * Hover only where hovering is real: pointer-fine devices at the desktop
 * breakpoint. On touch, tapping the summary is the only way in — which is what
 * a tap on a hover-opened menu would do anyway.
 */
class HeaderNav extends HTMLElement {
  connectedCallback() {
    this.openDelay = 60;
    this.closeDelay = 200;
    this.openTimer = null;
    this.closeTimer = null;

    /** Hover is a desktop pointer affordance, not a screen-width one. */
    this.pointerQuery = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');

    // pointerover bubbles, so one listener covers every item. `toggle` does
    // not bubble, hence the capture listener for that one.
    this.addEventListener('pointerover', this.onItemEnter);
    this.addEventListener('pointerleave', this.onNavLeave);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('click', this.onLabelClick);
    this.addEventListener('toggle', this.onToggle, true);
  }

  disconnectedCallback() {
    this.clearTimers();
  }

  get items() {
    return Array.from(this.querySelectorAll('details.anew-nav__details'));
  }

  clearTimers() {
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
  }

  /** Keep `aria-expanded` truthful however the panel was opened. */
  onToggle = (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open));
  };

  onItemEnter = (event) => {
    if (!this.pointerQuery.matches) return;

    const details = event.target instanceof Element ? event.target.closest('details.anew-nav__details') : null;
    this.clearTimers();

    // Hovering a plain link (or the gap between items) closes what is open,
    // rather than leaving a stale panel hanging over the page.
    if (!details) {
      this.closeTimer = setTimeout(() => this.closeAll(), this.closeDelay);
      return;
    }

    // Already open — including every move inside its own panel, which lives
    // inside the same <details> and so resolves to it here.
    if (details.open) return;

    // A short delay so dragging the pointer across the nav on the way to the
    // cart does not flash every panel in turn.
    this.openTimer = setTimeout(() => {
      this.closeAll(details);
      details.open = true;
    }, this.openDelay);
  };

  /**
   * The top-level label is a link, so a click on it navigates and the panel
   * never opens. That is the point on a desktop pointer — hover already opened
   * it. Without one (a touchscreen at >=990px, where the drawer has not taken
   * over yet) it would make the panel reachable only through the caret, so
   * there the first tap opens and a second follows the link.
   *
   * Modified clicks are left alone: ctrl/cmd-click is "open in a new tab", and
   * the panel has nothing to do with it.
   */
  onLabelClick = (event) => {
    if (this.pointerQuery.matches) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const label = event.target instanceof Element ? event.target.closest('a.anew-nav__label') : null;
    if (!label) return;

    const details = label.closest('details.anew-nav__details');
    if (!details || details.open) return;

    event.preventDefault();
    this.closeAll(details);
    details.open = true;
  };

  onNavLeave = () => {
    if (!this.pointerQuery.matches) return;
    this.clearTimers();
    this.closeTimer = setTimeout(() => this.closeAll(), this.closeDelay);
  };

  onKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    const open = this.items.find((details) => details.open);
    if (!open) return;

    open.open = false;
    // Focus would otherwise land nowhere, since the element it was in is gone.
    open.querySelector('summary')?.focus();
  };

  closeAll(except) {
    this.items.forEach((details) => {
      if (details !== except) details.open = false;
    });
  }
}

customElements.define('header-nav', HeaderNav);
